'use client';
import { useState } from 'react';
import useText from '../hooks/useText';
import useAuto from '../hooks/useAuto';
import { useAlertModal, useRevisionConfig } from '#/hooks';
import { RevisionConfigComponents } from '../components/revision-config';
import { SelectItemType } from 'types/common';
import { useLocalTexts } from '#/hooks/use-text-local';
import { chageLocalText } from '../data/data';
import { ANON_TEXT_LIMIT_MESSAGE, spellerLength } from '#/data/constants';
import { guardAnonymousUsage } from '#/lib/anon-usage-guard';
import { useRestorePendingText } from '#/hooks/use-restore-pending-text';
import { trackEvent } from '#/lib/analytics';
import { toast } from 'react-toastify';
import { useDUMP } from '#/hooks/use-dump';
import { getAnalyze, getAnalyzeRaw } from '#/actions/server/fetch';
import type { PlanLimits } from '#/types/plan-limits';
import AnalyzeTextInputSection from './components/text-input-section';
import AnalyzeResultsSection from './components/results-section';

interface analyzeProps {
  customList: SelectItemType[];
  apiKey: string;
  isUser: boolean;
  planLimits?: PlanLimits | null;
}

export default function AnalyzeClient({
  customList,
  apiKey,
  isUser,
  planLimits = null
}: analyzeProps) {
  const { setDump } = useDUMP();
  const [anonRemaining, setAnonRemaining] = useState<number | null>(null);

  const { texts: localTexts, setTexts: setLocalTexts } = useLocalTexts();
  const { customDictionaryList } = useRevisionConfig();
  const { open, close } = useAlertModal();
  const [sentences, setSentences] = useState<any[]>([]);
  const [json, setJson] = useState<any>(null);

  const { state: text, setState: setText } = useText();
  // 배포 스큐·청크 회복으로 새로 불러온 경우 입력을 되돌려 놓는다 (#271, #273)
  useRestorePendingText(setText, text);
  const {
    autoJointing,
    autoSpacing,
    autoSplitSentence,
    withSense,
    raw,
    setAutoJointing,
    setAutoSpacing,
    setAutoSplitSentence,
    setWithSense,
    setRaw
  } = useAuto();

  async function analyzeCall(
    text: string,
    customDictionaryList: SelectItemType[]
  ) {
    // GA4: '분석하기' 실행. /correct 와 같은 이벤트에 tool 만 다르게 보내
    // 기능 간 사용률을 같은 축에서 비교한다.
    trackEvent('click_inspect', {
      tool: 'analyze',
      is_user: isUser,
      text_length: text.length,
      custom_dict_count: customDictionaryList.length
    });

    // 텍스트 길이 제한 (비로그인만)
    if (!isUser && text.length > spellerLength) {
      toast.error(ANON_TEXT_LIMIT_MESSAGE);
      return false;
    }

    // 로그인 사용자: 어절 쿼터 소진 체크 (어드민은 무제한, 단건결제 잔액 있으면 허용)
    if (
      isUser &&
      planLimits &&
      !planLimits.isAdmin &&
      planLimits.usage.eojeolRemaining !== null &&
      planLimits.usage.eojeolRemaining <= 0 &&
      (planLimits.usage.onetimeEojeolBalance ?? 0) <= 0
    ) {
      toast.error(
        '이번 달 어절 한도를 모두 사용했습니다. 플랜을 업그레이드하거나 단기 사용권을 구매해 주세요.'
      );
      return false;
    }

    // 비로그인 사용자: 서버 측 IP 기반 일일 호출 횟수 체크
    if (!isUser) {
      // 실패 원인별 처리·안내는 guardAnonymousUsage 안에 모아 두었다 (#271).
      const gate = await guardAnonymousUsage('analyze', text);
      if (gate.remaining !== null) setAnonRemaining(gate.remaining);
      if (!gate.proceed) return false;
    }

    const dicts = customDictionaryList.map((e) => e.value);
    const document = { content: text, language: 'kr_KR' };

    // raw 는 별도 API(AnalyzeSyntaxRaw)다. 후처리를 하지 않으므로 자동 띄어쓰기·
    // 붙여쓰기·사용자 사전을 아예 보내지 않는다(요청 메시지에 그 필드가 없다).
    const res = raw
      ? await getAnalyzeRaw(apiKey, autoSplitSentence, 2, document, withSense)
      : await getAnalyze(
          apiKey,
          autoSplitSentence,
          autoSpacing,
          autoJointing,
          dicts.toString(),
          2,
          document,
          withSense
        );

    if (res && res?.ok) {
      const { ok, ...resWithoutOk } = res;
      if (!isUser) {
        // setCount(nowCount + res?.tokensCount || 0);
      } else {
        setDump();
      }
      chageLocalText(localTexts, text, setLocalTexts);
      setSentences(res.sentences); // 결과 저장
      setJson(resWithoutOk);
    } else {
      toast.error(res?.error);
    }
  }

  const handleDictSettings = () => {
    open({
      type: 'info',
      title: '사용자 사전 설정',
      description: '아래에서 적용할 사전 옵션을 선택해 주세요.',
      contents: (
        <RevisionConfigComponents
          apiKey={apiKey}
          customDictionaryList={customList}
          isEditFlow={false}
        />
      ),
      onConfirm: async () => {
        close();
      }
    });
  };

  const handleAnalyze = () => {
    analyzeCall(text, customDictionaryList);
  };

  const handleBack = () => {
    setSentences([]);
    setJson(null);
  };

  return (
    <>
      {sentences.length === 0 ? (
        <AnalyzeTextInputSection
          isUser={isUser}
          text={text}
          setText={setText}
          localTexts={localTexts}
          setLocalTexts={setLocalTexts}
          onAnalyze={handleAnalyze}
          onDictSettings={handleDictSettings}
          anonRemaining={anonRemaining}
          planLimits={planLimits}
          autoJointing={autoJointing}
          autoSpacing={autoSpacing}
          autoSplitSentence={autoSplitSentence}
          withSense={withSense}
          raw={raw}
          setAutoJointing={setAutoJointing}
          setAutoSpacing={setAutoSpacing}
          setAutoSplitSentence={setAutoSplitSentence}
          setWithSense={setWithSense}
          setRaw={setRaw}
        />
      ) : (
        <AnalyzeResultsSection
          sentences={sentences}
          json={json}
          onBack={handleBack}
          withSense={withSense}
        />
      )}
    </>
  );
}
